import { TYPE, type MessageFormatElement } from "@formatjs/icu-messageformat-parser";
import enCore from "@paginae/i18n/locales/en.json?part=core";

/** Catalogues are compiled to ICU message ASTs at build time (vite.config.ts). */
export type Messages = Record<string, MessageFormatElement[]>;

const en = enCore as unknown as Messages;

/** The text of a message without placeholders, e.g. a list of search keywords. */
export function plainText(message: MessageFormatElement[] | undefined): string {
  return (message ?? []).map((part) => (part.type === TYPE.literal ? part.value : "")).join("");
}

/** A published translation the interface is built from (from `/api/config`). */
export interface MessageBundle {
  tag: string;
  version: number;
}

/**
 * Catalogues come in two parts (messages-split.ts): the core part for the startup screens, and the
 * rest, loaded just after. English comes with the app; other languages are published from admin →
 * Translations (ADR-0016) and fetched as compiled, immutable bundles. Each language is layered
 * over its fallbacks and English, so missing messages fall back (docs/plan/06-i18n.md §5.1).
 */
async function fetchPart(bundle: MessageBundle, part: "core" | "rest"): Promise<Messages> {
  try {
    const res = await fetch(
      `/api/i18n/${encodeURIComponent(bundle.tag)}/${bundle.version}/${part}.json`,
    );
    return res.ok ? ((await res.json()) as Messages) : {};
  } catch {
    // Offline or failing: the interface shows the fallback languages' messages instead.
    return {};
  }
}

/** Merges bundles listed most specific first over `base`. */
async function layered(
  base: Messages,
  bundles: readonly MessageBundle[],
  part: "core" | "rest",
): Promise<Messages> {
  const parts = await Promise.all(bundles.map((b) => fetchPart(b, part)));
  return Object.assign({}, base, ...parts.reverse()) as Messages;
}

/**
 * Teaches `Intl` a language the runtime has no data for (Latin, Ancient Greek…), from the locale
 * data published with its translations (ADR-0017). Loaded only for such languages.
 */
export async function installPublishedLocaleData(bundle: MessageBundle | null): Promise<void> {
  if (!bundle) return;
  try {
    const res = await fetch(
      `/api/i18n/${encodeURIComponent(bundle.tag)}/${bundle.version}/locale.json`,
    );
    if (!res.ok) return;
    const [data, { installLocaleData }] = await Promise.all([
      res.json(),
      import("@paginae/i18n/locale-data"),
    ]);
    installLocaleData(bundle.tag, data);
  } catch {
    // Dates and numbers then show in the base formats of the runtime's nearest locale.
  }
}

/** The messages the startup screens need. */
export function loadMessages(bundles: readonly MessageBundle[]): Promise<Messages> {
  return layered(en, bundles, "core");
}

/** Every other message (admin, account, editor, dialogs…), in English and in the locale. */
export async function loadRestMessages(bundles: readonly MessageBundle[]): Promise<Messages> {
  const english = (await import("@paginae/i18n/locales/en.json?part=rest")).default as Messages;
  return layered(english, bundles, "rest");
}

let markReady: () => void = () => {};
/** Settles once every message is available: screens loaded on demand wait for it. */
export const allMessagesReady = new Promise<void>((resolve) => {
  markReady = resolve;
});
export const markAllMessagesReady = () => markReady();

/** A module loaded on demand, ready only when its messages are too. */
export function withMessages<T>(load: () => Promise<T>): () => Promise<T> {
  return () => Promise.all([load(), allMessagesReady]).then(([module]) => module);
}

export function applyDocumentLocale(locale: string, dir: "ltr" | "rtl"): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = dir;
}
